// Core
import React, { useCallback, useState } from 'react';

// Libraries
import { MenuItemType } from 'antd/es/menu/hooks/useItems';
import { useTranslation } from 'react-i18next';

// Media
// TODO: Uncomment media when implemented
// import ComboUnitImage from '../../../../../media/images/maintenance/ComboUnit/ComboUnit.png';
// import IFMStatus from '../../../../../media/images/maintenance/ifm.png';
// import Plc from '../../../../../media/images/maintenance/plc.png';

// Components
import { MachineCard } from '../../../../../components/MachineCard/MachineCard';
import { ModalPanel, ModalSize } from '../../../../../layout/elements/ModalPanel';
import { Paragraph, ParagraphWeight } from '../../../../../layout/elements/Paragraph';
import {
  CheckboxLine,
  RouterLine,
  Thermometer,
} from '../../../../../layout/icons';
// TODO: Uncomment diagnostics cards when implemented
// import { MaintenanceCard } from '../../../../../components/MaintenanceCard';

// Modules
import { SensorScaling } from './SensorScaling';
import { Setpoint, SetpointType } from './Setpoint';
import { Setup } from './Setup';

// View
import { MaintenanceProps, MaintenanceType } from './Maintenance.types';
import {
  CardContainer,
  CardContent,
  Container,
  // DiagnosticsCardsContainer, // TODO: Uncomment diagnostics cards when implemented
  // DiagnosticsSection, // TODO: Uncomment diagnostics cards when implemented
} from './Maintenance.style';

function Maintenance({
  className,
  edgeStatus,
  onClose,
  isOpen,
  testId,
  unitData,
}: MaintenanceProps): JSX.Element | null {
  // Dependencies
  const { t } = useTranslation();

  /* ***********************************************************************************************
  **************************************** INITIAL STATE *******************************************
  *********************************************************************************************** */

  type State = {
    selectedSetPointView: SetpointType;
    currentView: MaintenanceType;
  };

  const initialState: State = {
    selectedSetPointView: SetpointType.crusher,
    currentView: MaintenanceType.maintenance,
  };

  const [state, setState] = useState<State>(initialState);

  /* ***********************************************************************************************
  ************************************** COMPONENT HANDLING ****************************************
  *********************************************************************************************** */

  /* Update selected set point view */
  const handleSetPoint = useCallback((item: MenuItemType): void => {
    const setPointView: SetpointType | undefined = Object.values(SetpointType).find(
      (value: SetpointType) => value === item.key,
    );

    setState((prevState: State) => ({
      ...prevState,
      selectedSetPointView: setPointView || SetpointType.crusher,
    }));
  }, []);

  /* Handle view change */
  const handleViewChange = useCallback((view: MaintenanceType): void => {
    setState((prevState: State) => ({
      ...prevState,
      currentView: view,
    }));
  }, []);

  const handleClose = useCallback((): void => {
    setState((prevState: State) => ({
      ...prevState,
      currentView: MaintenanceType.maintenance,
    }));
    onClose();
  }, [onClose]);

  /* Render content based on the current view */
  let content: JSX.Element | null = null;

  const titleMap: Record<MaintenanceType, string> = {
    [MaintenanceType.comboUnit]: t('view.crusher.maintenance.comboUnit.title'),
    [MaintenanceType.cone]: t('view.crusher.maintenance.cone'),
    [MaintenanceType.ifm]: 'IFM / ASI',
    [MaintenanceType.maintenance]: t('view.crusher.maintenance.title'),
    [MaintenanceType.plc]: t('view.crusher.maintenance.plc'),
    [MaintenanceType.sensorScaling]: t('common.sensorScaling'),
    [MaintenanceType.setPoint]: t('common.setPoint'),
    [MaintenanceType.setup]: t('common.setup'),
  };

  if (state.currentView === MaintenanceType.maintenance) {
    content = (
      <Container className={className} data-testid={testId}>
        <Paragraph weight={ParagraphWeight.bold}>
          {t('view.crusher.maintenance.description')}
        </Paragraph>
        <CardContainer>
          <CardContent>
            <MachineCard
              description={t('common.setup')}
              icon={<CheckboxLine />}
              onClick={() => handleViewChange(MaintenanceType.setup)}
            />
          </CardContent>
          <CardContent>
            <MachineCard
              description={t('common.setPoint')}
              icon={<RouterLine />}
              onClick={() => handleViewChange(MaintenanceType.setPoint)}
            />
          </CardContent>
          <CardContent>
            <MachineCard
              description={t('common.sensorScaling')}
              icon={<Thermometer />}
              onClick={() => handleViewChange(MaintenanceType.sensorScaling)}
            />
          </CardContent>
        </CardContainer>
        {/* <DiagnosticsSection> // TODO: Uncomment diagnostics cards when implemented
          <Paragraph weight={ParagraphWeight.bold}>
            {t('common.hmiDiagnostics')}
          </Paragraph>
          <DiagnosticsCardsContainer>
            <MaintenanceCard
              className={className}
              imgSrc={ComboUnitImage}
              testId="ComboUnit"
              text={t('view.crusher.maintenance.comboUnit.comboUnitCard')}
              onClick={() => onChangeView(MaintenanceType.comboUnit)}
            />
            <MaintenanceCard
              className={className}
              imgSrc={Plc}
              testId="plc"
              text={t('view.crusher.maintenance.plc')}
              onClick={() => onChangeView(MaintenanceType.plc)}
            />
            <MaintenanceCard
              className={className}
              imgSrc={IFMStatus}
              testId="ifm"
              text="IFM / ASI"
              onClick={() => onChangeView(MaintenanceType.ifm)}
            />
          </DiagnosticsCardsContainer>
        </DiagnosticsSection> */}
      </Container>
    );
  } else if (state.currentView === MaintenanceType.setup) {
    content = (
      <Setup
        unitData={unitData}
        edgeStatus={edgeStatus}
      />
    );
  } else if (state.currentView === MaintenanceType.sensorScaling) {
    content = (
      <SensorScaling
        unitData={unitData}
      />
    );
  } else if (state.currentView === MaintenanceType.setPoint) {
    content = (
      <Setpoint
        onChangeView={handleSetPoint}
        unitData={unitData}
        edgeStatus={edgeStatus}
        selectedView={state.selectedSetPointView}
      />
    );
  }

  return (
    <ModalPanel
      onClose={handleClose}
      showModal={isOpen}
      size={ModalSize.xl}
      title={titleMap[state.currentView]}
      onBack={
        state.currentView !== MaintenanceType.maintenance
          ? () => handleViewChange(MaintenanceType.maintenance)
          : false
      }
    >
      {content}
    </ModalPanel>
  );
}

export { Maintenance };
